import { useState, ChangeEvent, useEffect, useContext } from 'react';

import { TextField, FormControl } from '@material-ui/core';
import { gql, useLazyQuery } from '@apollo/client';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import useDebounce from 'react-use/lib/useDebounce';
import { UserContext } from '../../contexts/UserContext';

/**
 * The schema for the {@link ActiveDirectoryGroupPickerMulti} properties.
 */
export const ActiveDirectoryGroupPickerMultiSchema: CustomFieldExtensionSchema =
  {
    returnValue: {
      type: 'array',
      items: { type: 'string' },
    },
  };

const GroupsQuery = gql`
  query GetUserGroups($login: String!, $filter: GroupListFilter) {
    identity {
      user(login: $login) {
        memberOf(filter: $filter) {
          name
        }
      }
    }
  }
`;

export type ActiveDirectoryGroupState = FieldExtensionComponentProps<
  string[] | undefined
>;

/**
 * The underlying component that is rendered in the form for the `ActiveDirectoryPickerMulti`
 * field extension.
 * @param props - The properties passed to the component.
 * @public
 */
export const ActiveDirectoryGroupPickerMulti = (
  props: ActiveDirectoryGroupState,
) => {
  const {
    formData = [],
    onChange,
    rawErrors,
    idSchema,
    required,
    uiSchema,
  } = props;
  const maxSelection = Number(uiSchema?.['ui:options']?.maxSelection) || 5;

  const [groups, setGroups] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [grpName, setGrpName] = useState<string | undefined>(undefined);
  const { login } = useContext(UserContext);

  const [fetchGroups, { loading, error }] = useLazyQuery(GroupsQuery, {
    errorPolicy: 'all',
    variables: { login: login, filter: { name: grpName } },
  });

  useEffect(() => {
    if (login && grpName) {
      fetchGroups()
        .then(groupResults => {
          if (groupResults?.data) {
            setGroups(
              groupResults.data?.identity?.user?.memberOf?.map(
                (group: { name: string }) => group?.name,
              ) || [],
            );
          }
        })
        .catch((err: unknown) => {
          return err;
        });
    }
  }, [grpName, fetchGroups, login]);

  useDebounce(() => setGrpName(inputValue), 500, [inputValue]);

  const onHandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const onSelect = (_: any, value: string[]) => {
    if (value.length <= maxSelection) {
      onChange(value);
    }
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && formData.length < 1}
    >
      {error ? (
        <TextField
          error
          label="Error"
          defaultValue="No Groups Available"
          helperText="Failed to fetch group details."
          variant="outlined"
        />
      ) : (
        <Autocomplete
          multiple
          id={idSchema?.$id}
          value={formData}
          loading={loading}
          loadingText="Loading.."
          options={groups}
          getOptionLabel={option => option}
          onChange={onSelect}
          renderInput={params => (
            <TextField
              {...params}
              label={`Select Groups (Max ${maxSelection})`}
              margin="normal"
              helperText={
                formData.length >= maxSelection
                  ? `Maximum ${maxSelection} AD groups can be selected`
                  : 'Select AD groups associated with the user'
              }
              variant="outlined"
              required={formData.length < 1}
              onChange={onHandleChange}
              InputProps={params.InputProps}
              value={inputValue}
            />
          )}
        />
      )}
    </FormControl>
  );
};
